import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Container, Row, Col } from 'react-bootstrap';
import AOS from 'aos';
import {
  FaArrowRight, FaBriefcase, FaMapMarkerAlt, FaClock,
  FaCheckCircle, FaRocket, FaGraduationCap, FaHeart, FaUsers
} from 'react-icons/fa';

const OPENINGS = [
  { title: 'Senior Full-Stack Developer', dept: 'Engineering', location: 'Chennai / Remote', type: 'Full-Time', skills: ['ReactJS / Angular', '.NET Core', 'PostgreSQL', 'AWS'] },
  { title: 'AI/ML Engineer', dept: 'AI Division', location: 'Chennai / Remote', type: 'Full-Time', skills: ['Python', 'TensorFlow', 'NLP', 'OpenAI API'] },
  { title: 'UI/UX Designer', dept: 'Product & Design', location: 'Chennai', type: 'Full-Time', skills: ['Figma', 'Adobe XD', 'Prototyping', 'Design Systems'] },
  { title: 'Digital Marketing Executive', dept: 'PromoX', location: 'Chennai', type: 'Full-Time', skills: ['SEO', 'Google Ads', 'Meta Ads', 'Analytics'] },
  { title: 'IT Recruiter', dept: 'TalentForce', location: 'Chennai / Remote', type: 'Full-Time', skills: ['Sourcing', 'Screening', 'ATS Tools', 'Networking'] },
  { title: 'DevOps Engineer', dept: 'Engineering', location: 'Remote', type: 'Contract', skills: ['Docker', 'Kubernetes', 'AWS', 'CI/CD'] },
];

const PERKS = [
  { icon: <FaRocket />, title: 'Flexible Work Hours', desc: 'We trust you to manage your time effectively.' },
  { icon: <FaGraduationCap />, title: 'Learning Budget', desc: 'Annual budget for courses, certifications, and conferences.' },
  { icon: <FaHeart />, title: 'Health Insurance', desc: 'Comprehensive medical coverage for you and family.' },
  { icon: <FaUsers />, title: 'Team Retreats', desc: 'Quarterly offsites to build culture and celebrate wins.' },
];

const DEPT_COLORS = {
  'Engineering': { bg: 'rgba(30,64,175,0.1)', color: '#1e40af' },
  'AI Division': { bg: 'rgba(124,58,237,0.1)', color: '#7c3aed' },
  'Product & Design': { bg: 'rgba(8,145,178,0.1)', color: '#0891b2' },
  'PromoX': { bg: 'rgba(220,38,38,0.1)', color: '#dc2626' },
  'TalentForce': { bg: 'rgba(5,150,105,0.1)', color: '#059669' },
};

const Careers = () => {
  useEffect(() => {
    AOS.init({ duration: 900, once: true, offset: 80 });
  }, []);

  return (
    <div style={{ background: 'var(--color-bg)' }}>
      {/* Page Hero */}
      <section className="page-hero">
        <div className="page-hero-overlay" />
        <Container>
          <div className="page-hero-content" data-aos="fade-up">
            <div className="breadcrumb-vk">
              <Link to="/">Home</Link>
              <span>/</span>
              <span>Careers</span>
            </div>
            <h1 className="page-hero-title">
              Join the <span>Vkomak Team</span>
            </h1>
            <p className="page-hero-desc">
              We're building the future of enterprise software. If you're passionate about technology, innovation, and impact — we want to hear from you.
            </p>
            <div style={{ marginTop: '2rem', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
              <a href="#openings" className="btn-primary-vk">View Openings <FaArrowRight /></a>
              <Link to="/contact" className="btn-outline-vk">Send Resume</Link>
            </div>
          </div>
        </Container>
      </section>

      {/* Perks */}
      <section className="section-py" style={{ background: 'var(--color-bg-alt)' }}>
        <Container>
          <div className="section-header text-center mb-5" data-aos="fade-up">
            <span className="section-badge">Why Work With Us</span>
            <h2 className="section-title">Perks & <span>Benefits</span></h2>
            <p className="section-subtitle">We invest in our people because great products come from great teams.</p>
          </div>
          <Row className="g-4">
            {PERKS.map((p, i) => (
              <Col md={6} lg={3} key={i} data-aos="fade-up" data-aos-delay={i * 80}>
                <div className="vk-card text-center" style={{ height: '100%' }}>
                  <div style={{
                    width: 56, height: 56,
                    borderRadius: 14,
                    background: 'linear-gradient(135deg, rgba(30,64,175,0.1), rgba(8,145,178,0.08))',
                    display: 'grid', placeItems: 'center',
                    fontSize: '1.5rem', color: 'var(--color-primary)',
                    margin: '0 auto 1rem'
                  }}>
                    {p.icon}
                  </div>
                  <h5 style={{ color: 'var(--color-text)', fontWeight: 700, marginBottom: '0.5rem' }}>{p.title}</h5>
                  <p style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem', margin: 0 }}>{p.desc}</p>
                </div>
              </Col>
            ))}
          </Row>

          {/* Extra perks pills */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', justifyContent: 'center', marginTop: '2.5rem' }} data-aos="fade-up">
            {['Remote-Friendly', 'Performance Bonuses', 'Stock Options (Senior Roles)', 'Mentorship Programs', 'Team Lunches', 'Hackathons'].map((p, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '8px 20px',
                background: 'var(--color-bg-card)',
                border: '1px solid var(--color-border)',
                borderRadius: 999,
                fontSize: '0.875rem', fontWeight: 500,
                color: 'var(--color-text)'
              }}>
                <FaCheckCircle style={{ color: '#059669', fontSize: '0.8rem' }} /> {p}
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* Open Positions */}
      <section id="openings" className="section-py" style={{ background: 'var(--color-bg)' }}>
        <Container>
          <div className="section-header text-center mb-5" data-aos="fade-up">
            <span className="section-badge">Open Positions</span>
            <h2 className="section-title">Current <span>Openings</span></h2>
            <p className="section-subtitle">Explore our open positions and find your next career opportunity.</p>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {OPENINGS.map((job, i) => {
              const dc = DEPT_COLORS[job.dept] || { bg: 'rgba(30,64,175,0.1)', color: '#1e40af' };
              return (
                <div
                  key={i}
                  className="vk-card"
                  style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', flexWrap: 'wrap' }}
                  data-aos="fade-up"
                  data-aos-delay={i * 60}
                >
                  <div style={{
                    width: 52, height: 52, borderRadius: 12,
                    background: dc.bg, color: dc.color,
                    display: 'grid', placeItems: 'center',
                    fontSize: '1.375rem', flexShrink: 0
                  }}>
                    <FaBriefcase />
                  </div>
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <div style={{ fontSize: '1.0625rem', fontWeight: 700, color: 'var(--color-text)', marginBottom: 6 }}>
                      {job.title}
                    </div>
                    <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
                      <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
                        <FaMapMarkerAlt /> {job.location}
                      </span>
                      <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
                        <FaClock /> {job.type}
                      </span>
                      <span style={{
                        fontSize: '0.8125rem',
                        background: dc.bg, color: dc.color,
                        padding: '3px 12px', borderRadius: 999, fontWeight: 600
                      }}>
                        {job.dept}
                      </span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    {job.skills.map((s, si) => (
                      <span key={si} style={{
                        fontSize: '0.75rem',
                        background: 'var(--color-bg-alt)',
                        border: '1px solid var(--color-border)',
                        padding: '4px 10px', borderRadius: 999,
                        color: 'var(--color-text-muted)', fontWeight: 500
                      }}>
                        {s}
                      </span>
                    ))}
                  </div>
                  <Link
                    to="/contact"
                    className="btn-secondary-vk"
                    style={{ whiteSpace: 'nowrap', padding: '10px 20px', fontSize: '0.875rem' }}
                  >
                    Apply Now <FaArrowRight />
                  </Link>
                </div>
              );
            })}
          </div>
        </Container>
      </section>

      {/* CTA */}
      <section className="cta-section">
        <Container className="text-center" data-aos="zoom-in">
          <h2 className="cta-title">Don't See Your <span>Role?</span></h2>
          <p className="cta-desc">Send us your resume and we'll reach out when a matching position opens up.</p>
          <div className="cta-actions">
            <Link to="/contact" className="btn-primary-vk">Send Your Resume <FaArrowRight /></Link>
          </div>
        </Container>
      </section>
    </div>
  );
};

export default Careers;
